import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Card as ApiCard, getCardsByBoard, CardList } from '../../api';
import Button from '../common/Button';

// --- 스타일 정의 ---
const Overlay = styled.div<{ isOpen: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9;
    opacity: ${props => props.isOpen ? 1 : 0};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease-in-out;
`;
const SidebarContainer = styled.div<{ isOpen: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 350px;
    height: 100vh;
    background-color: var(--bg-header);
    box-shadow: 4px 0 10px var(--shadow-strong);
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease-in-out;
    z-index: 10;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
`;
const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
`;
const Title = styled.h2`
    color: var(--text-bright);
`;
const CloseButton = styled(Button)`
    background: transparent;
    font-size: 1.5rem;
    padding: 0;
    &:hover { background: var(--border-color); }
`;
const CardListWrapper = styled.div`
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 0.5rem;
    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
        background-color: var(--text-dark);
        border-radius: 4px;
        border: 2px solid var(--bg-header);
    }
`;
const CardItem = styled.div`
    background-color: var(--bg-card);
    padding: 1rem;
    border-radius: 4px;
    border: 1px solid var(--border-color);
    display: flex;
    gap: 0.75rem;
    opacity: 0.8;
    &:not(:last-child) { margin-bottom: 0.5rem; }
    &:hover { background-color: var(--border-color); opacity: 1; }
`;
const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 1;
    cursor: pointer;
    &:hover h3 { color: var(--primary-accent); }
`;
const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;
const CardTitle = styled.h3`
    color: var(--text-normal);
    text-decoration: line-through;
    transition: color 0.2s;
`;
const ListName = styled.span`
    font-size: 0.8rem;
    color: var(--text-dark);
    font-weight: bold;
`;
const CardLabelsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
`;
const CardLabelPill = styled.div<{ color: string }>`
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: bold;
    color: white;
    background-color: ${props => props.color};
`;
const CardMetaContainer = styled.div`
    font-size: 0.8rem;
    color: var(--text-dark);
`;
const CompleteCheckbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid var(--text-dark);
    border-radius: 50%;
    margin-top: 2px;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease-in-out;
    &.completed {
        background-color: #98c379;
        border-color: #98c379;
    }
`;
const CheckIcon = styled.svg`
    width: 12px;
    height: 12px;
    stroke: white;
    stroke-width: 2;
`;

// --- 타입 정의 ---
interface OutboxSidebarProps {
    isOpen: boolean;
    boardId: string;
    lists: CardList[];
    onClose: () => void;
    onViewCard: (cardId: string) => void;
    onToggleComplete: (card: ApiCard) => void;
}

const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return dateString.split(' ')[0];
};

const OutboxSidebar: React.FC<OutboxSidebarProps> = ({ isOpen, boardId, lists, onClose, onViewCard, onToggleComplete }) => {
    const [completedCards, setCompletedCards] = useState<ApiCard[]>([]);

    const fetchCompletedCards = useCallback(async () => {
        if (isOpen) {
            try {
                const response = await getCardsByBoard(boardId, { completed: true });
                setCompletedCards(response.data._embedded?.getList || []);
            } catch (error) {
                console.error("Failed to fetch completed cards", error);
            }
        }
    }, [boardId, isOpen]);

    useEffect(() => {
        fetchCompletedCards();
    }, [fetchCompletedCards, isOpen]);

    const handleCardClick = (cardId: string) => {
        onViewCard(cardId);
    };

    const handleUncomplete = (e: React.MouseEvent, card: ApiCard) => {
        e.stopPropagation();
        onToggleComplete(card);
        setCompletedCards(prev => prev.filter(c => c.id !== card.id));
    };

    const findListName = (listId: string) => {
        return lists.find(l => l.id === listId)?.name || 'Unknown List';
    };

    return (
        <>
            <Overlay isOpen={isOpen} onClick={onClose} />
            <SidebarContainer isOpen={isOpen}>
                <Header>
                    <Title>Outbox</Title>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </Header>
                <CardListWrapper>
                    {completedCards.length > 0 ? (
                        completedCards.map(card => {
                            const startDate = formatDate(card.startDate);
                            const dueDate = formatDate(card.dueDate);
                            return (
                                <CardItem key={card.id}>
                                    <CompleteCheckbox className="completed" onClick={(e) => handleUncomplete(e, card)}>
                                        <CheckIcon viewBox="0 0 24 24"><path fill="none" d="M20 6L9 17l-5-5"/></CheckIcon>
                                    </CompleteCheckbox>
                                    <CardContent onClick={() => handleCardClick(card.id)}>
                                        <CardHeader>
                                            <CardTitle>{card.title}</CardTitle>
                                            <ListName>in list: {findListName(card.cardListId)}</ListName>
                                        </CardHeader>
                                        {card.labels && card.labels.length > 0 && (
                                            <CardLabelsContainer>
                                                {card.labels.map(label => (
                                                    <CardLabelPill key={label.name} color={label.color}>
                                                        {label.name}
                                                    </CardLabelPill>
                                                ))}
                                            </CardLabelsContainer>
                                        )}
                                        {(startDate || dueDate) && (
                                            <CardMetaContainer>
                                                <span>{startDate || '...'} ~ {dueDate || '...'}</span>
                                            </CardMetaContainer>
                                        )}
                                    </CardContent>
                                </CardItem>
                            )
                        })
                    ) : (
                        <p>No completed cards yet.</p>
                    )}
                </CardListWrapper>
            </SidebarContainer>
        </>
    );
};

export default OutboxSidebar;

